/**
 * Client-side compression for generated images.
 *
 * The gallery deliberately caps one data URL at 2 MB, while 1024x1024 PNG
 * output from modern image models is commonly 2–4 MB. Re-encode oversized
 * results to WebP (with a JPEG fallback) before they reach IndexedDB so the
 * visible "已存入图库" promise stays true without raising the storage budget.
 */
import type { GeneratedImage } from '../../core/image';
import { DEFAULT_MAX_DATA_URL_BYTES, estimateDataUrlBytes } from './save';

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('IMAGE_DECODE_FAILED'));
    image.src = dataUrl;
  });
}

function encode(
  image: HTMLImageElement,
  width: number,
  height: number,
  type: string,
  quality: number,
): string | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) return null;
  context.drawImage(image, 0, 0, width, height);
  const encoded = canvas.toDataURL(type, quality);
  return encoded.startsWith(`data:${type}`) ? encoded : null;
}

export async function compressGeneratedImage(
  image: GeneratedImage,
  maxBytes: number = DEFAULT_MAX_DATA_URL_BYTES,
): Promise<GeneratedImage> {
  if (estimateDataUrlBytes(image.dataUrl) <= maxBytes) return image;
  if (typeof document === 'undefined' || typeof Image === 'undefined') return image;
  let decoded: HTMLImageElement;
  try {
    decoded = await loadImage(image.dataUrl);
  } catch {
    return image;
  }
  const naturalWidth = decoded.naturalWidth;
  const naturalHeight = decoded.naturalHeight;
  if (naturalWidth <= 0 || naturalHeight <= 0) return image;

  for (const scale of [1, 0.85, 0.7, 0.55]) {
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));
    for (const [type, quality] of [
      ['image/webp', 0.9],
      ['image/webp', 0.8],
      ['image/jpeg', 0.85],
      ['image/jpeg', 0.75],
    ] as const) {
      let encoded: string | null = null;
      try {
        encoded = encode(decoded, width, height, type, quality);
      } catch {
        encoded = null;
      }
      if (encoded !== null && estimateDataUrlBytes(encoded) <= maxBytes) {
        return {
          dataUrl: encoded,
          mime: type,
          ...(image.revisedPrompt === undefined ? {} : { revisedPrompt: image.revisedPrompt }),
        };
      }
    }
  }
  return image;
}

export async function compressGeneratedImages(
  images: readonly GeneratedImage[],
  maxBytes: number = DEFAULT_MAX_DATA_URL_BYTES,
): Promise<GeneratedImage[]> {
  return Promise.all(images.map((image) => compressGeneratedImage(image, maxBytes)));
}
